/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';

import {
  Button,
  DateInput,
  Form,
  Icon,
  Modal,
  Tooltip,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { API_DATE_FORMAT, URL_ACTE_ADRESSES } from '@/constants';
import { ChampLieu } from '@/components/Common';
import {
  buildEvenementMariageUpdatePayload,
  buildLieuActeUpdatePayload,
  createTimeInputHandler,
  parseLieuActe,
} from '@/utils';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit (cf. `validationInformationsNaissance`).
dayjs.extend(customParseFormat);

interface ModalEvenementMariageProps {
  open: boolean;
  /** Identifiant de l'acte : construit l'URL de `service.update`. */
  identifiant?: number;
  /** L'acte : date et heure de la célébration, et le lieu imbriqué. */
  acte?: any;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (acte: any) => void;
}

/**
 * Bloc « INFORMATIONS SUR LE MARIAGE » en édition : date et heure de la
 * célébration, et lieu.
 *
 * Deux tables, donc deux appels :
 *
 * - le LIEU est une ligne `ec_acte_adresse` → `PUT /acte-adresses/{id}` ;
 * - date et heure sont sur l'acte → `PUT /numerisation-actes/mariage/{id}`.
 *
 * ⚠️ On écrit le LIEU d'abord, puis l'ACTE dans son `.then` — jamais en parallèle
 * (cf. `CLAUDE.md`, `request` n'est pas une `Promise`). L'ordre rend aussi
 * l'échec lisible : le premier appel a abouti, l'erreur est explicite.
 *
 * ⚠️ Le CONTRAT et son notaire ne sont PAS ici : la maquette leur donne un
 * panneau propre, édité par `ModalContratMariage`.
 */
const ModalEvenementMariage = (props: ModalEvenementMariageProps) => {
  const { open, identifiant, acte, service, onClose, onSaved } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData({
      dateMariage: acte?.dateMariage,
      heureMariage: acte?.heureMariage,
      minuteMariage: acte?.minuteMariage,
      adresseMariage: parseLieuActe(acte, 'lieuMariage'),
    });
    setAttempted(false);
  }, [open, acte]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(handleChange),
    [handleChange],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      handleChange(null, {
        name: 'dateMariage',
        value: _.isNil(date) ? null : dayjs(date).format(API_DATE_FORMAT),
      });
    },
    [handleChange],
  );

  const errors = React.useMemo(() => {
    const e: Record<string, string> = {};

    if (_.isEmpty(data?.dateMariage)) {
      e.dateMariage = 'La date du mariage est obligatoire';
    }

    if (_.isEmpty(data?.adresseMariage?.libelleCommune)) {
      e.adresseMariage = 'Le lieu du mariage est obligatoire';
    }

    return e;
  }, [data]);

  const displayedErrors = attempted ? errors : {};

  /** Enregistre l'acte, et referme. */
  const enregistrerActe = React.useCallback(() => {
    const payload = buildEvenementMariageUpdatePayload(
      identifiant as number,
      data,
    );

    const enregistrement = service?.update(payload);

    // Pas de service : le lieu est déjà écrit, on referme sans mentir.
    if (!enregistrement) {
      setSaving(false);
      onClose();
      return;
    }

    enregistrement
      .then(() => {
        onSaved({ ...acte, ...payload.acte });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement du mariage",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [service, identifiant, acte, data, onSaved, onClose]);

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    setSaving(true);

    const idLieu = acte?.lieuMariage?.identifiant;

    // Aucun lieu en base : rien à corriger, on va droit à l'acte. Un acte
    // numérisé en a toujours un, ce cas ne vise que le fonds repris incomplet.
    if (_.isNil(idLieu)) {
      enregistrerActe();
      return;
    }

    request
      .put(
        `${URL_ACTE_ADRESSES}/${idLieu}`,
        buildLieuActeUpdatePayload(data?.adresseMariage),
      )
      .then(() => {
        enregistrerActe();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement du lieu du mariage",
        );
        setSaving(false);
      });
  }, [errors, acte, data, enregistrerActe]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Modifier les informations du mariage" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              gap: '1em',
            }}
          >
            <Form.Field required error={!_.isNil(displayedErrors.dateMariage)}>
              <label>Marié le</label>
              <Tooltip
                trigger={
                  <DateInput
                    value={
                      data?.dateMariage
                        ? dayjs(data.dateMariage, API_DATE_FORMAT).toDate()
                        : undefined
                    }
                    onChange={handleDateChange}
                  />
                }
                type={displayedErrors.dateMariage ? 'error' : ''}
                message={displayedErrors.dateMariage}
              />
            </Form.Field>
            <Form.Group style={{ alignItems: 'flex-end' }}>
              <Form.Input
                label="à"
                placeholder="hh"
                name="heureMariage"
                style={{ width: 48 }}
                value={
                  !_.isNil(data?.heureMariage)
                    ? String(data.heureMariage).padStart(2, '0')
                    : ''
                }
                onChange={handleTimeInput}
              />
              <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>
                :
              </span>
              <Form.Input
                placeholder="mm"
                name="minuteMariage"
                style={{ width: 48 }}
                value={
                  !_.isNil(data?.minuteMariage)
                    ? String(data.minuteMariage).padStart(2, '0')
                    : ''
                }
                onChange={handleTimeInput}
              />
            </Form.Group>
          </div>
          <ChampLieu
            data={data}
            errors={{ adresseMariage: displayedErrors.adresseMariage }}
            onChange={handleChange}
            name="adresseMariage"
            title="Lieu du mariage"
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="evenement-mariage-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalEvenementMariage;
